import { OrganisationsService } from "@/client"
import type { OrganisationRead } from "@/client"
import useAuth from "@/hooks/useAuth"

export const ALL_ORGS_ID = "__all__"
export const ALL_ORGS: OrganisationRead = {
  id: ALL_ORGS_ID,
  name: "All Departments",
  enabled: true,
} as OrganisationRead

export const isAllOrgs = (org: OrganisationRead | null): boolean =>
  org?.id === ALL_ORGS_ID

import { useQuery } from "@tanstack/react-query"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

interface OrganizationContextType {
  /** The top-level organisation (parent_id == null) — the company itself. Null when no hierarchy is configured. */
  rootOrganization: OrganisationRead | null
  /** Orgs that have a parent_id set — the selectable departments. Falls back to all orgs when no hierarchy exists. */
  departments: OrganisationRead[]
  /** All orgs the user can see (departments + root if any). */
  organizations: OrganisationRead[]
  currentOrganization: OrganisationRead | null
  setCurrentOrganization: (org: OrganisationRead | null) => void
  isLoading: boolean
}

const OrganizationContext = createContext<OrganizationContextType>({
  rootOrganization: null,
  departments: [],
  organizations: [],
  currentOrganization: null,
  setCurrentOrganization: () => {},
  isLoading: false,
})

export { OrganizationContext }

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    )
  }
  return context
}

interface OrganizationProviderProps {
  children: ReactNode
}

export const OrganizationProvider = ({
  children,
}: OrganizationProviderProps) => {
  const { user } = useAuth()
  const [currentOrganization, setCurrentOrganizationState] =
    useState<OrganisationRead | null>(() => {
      const stored = localStorage.getItem("current-organization")
      return stored ? JSON.parse(stored) : null
    })

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () =>
      OrganisationsService.readOrganisations({ skip: 0, limit: 100 }),
    enabled: !!user,
  })

  /** Orgs with no parent = the company/organisation level. */
  const rootOrganization = useMemo<OrganisationRead | null>(() => {
    const roots = organizations.filter((o) => !o.parent_id)
    const hasDepartments = organizations.some((o) => !!o.parent_id)
    // Only expose a root org when the hierarchy has actually been configured
    if (!hasDepartments || roots.length !== 1) return null
    return roots[0]
  }, [organizations])

  /** Selectable departments — orgs with a parent. Falls back to all orgs if no hierarchy. */
  const departments = useMemo<OrganisationRead[]>(() => {
    const depts = organizations.filter((o) => !!o.parent_id)
    return depts.length > 0 ? depts : organizations
  }, [organizations])

  const setCurrentOrganization = useCallback((org: OrganisationRead | null) => {
    setCurrentOrganizationState(org)
    if (org) {
      localStorage.setItem("current-organization", JSON.stringify(org))
    } else {
      localStorage.removeItem("current-organization")
    }
  }, [])

  // Auto-select first department when none is selected
  useEffect(() => {
    if (departments.length > 0 && !currentOrganization) {
      setCurrentOrganization(departments[0])
    }
  }, [departments, currentOrganization, setCurrentOrganization])

  // Re-validate stored selection against updated department list AND
  // refresh its fields (logo_url, name, description, parent_id, protected)
  // from the live API. Without this sync, a logo uploaded after the user's
  // last manual selection stays invisible because currentOrganization is
  // initialised from localStorage once and never re-hydrated from
  // `organizations` afterwards.
  useEffect(() => {
    if (
      currentOrganization &&
      !isAllOrgs(currentOrganization) &&
      departments.length > 0
    ) {
      const fresh = departments.find(
        (o) => o.id === currentOrganization.id,
      )
      if (!fresh) {
        setCurrentOrganization(departments[0])
        return
      }
      // `protected` is intentionally omitted here — it's a backend column
      // that the generated OpenAPI client hasn't been re-emitted for yet.
      // The visible-on-the-sidebar fields (logo, name, description, parent)
      // are enough to keep the cached selection in sync.
      if (
        fresh.logo_url !== currentOrganization.logo_url ||
        fresh.name !== currentOrganization.name ||
        fresh.description !== currentOrganization.description ||
        fresh.parent_id !== currentOrganization.parent_id
      ) {
        setCurrentOrganization(fresh)
      }
    }
  }, [departments, currentOrganization, setCurrentOrganization])

  return (
    <OrganizationContext.Provider
      value={{
        rootOrganization,
        departments,
        organizations,
        currentOrganization,
        setCurrentOrganization,
        isLoading,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}
